import AmendmentOriginalButton from "./AmendmentOriginalButton";
/**
 * HS 비교 (embedded) — UNIPASS 011Q 풍 나라별 HS 코드 나란히 비교.
 *   검색(HS 코드) → 선택 국가 컬럼별로 그 나라 세번 계층 + 세율 표시.
 *   데이터: 백엔드 프록시 /api/onto/hs-compare?q=&country= (UNIPASS 실시간).
 *   HS 6자리는 국제공통, 7~10자리는 국가별 세분 → 컬럼 비교로 차이 한눈에.
 */
import React, { useState, useEffect, useCallback } from "react";
import SourceEvidenceLink, {
  precedentEvidence,
} from "./SourceEvidenceLink";
import { userAuthHeaders } from "./userAuth";
import "./theme.css";

// 42개국 (백엔드 WORLD_HS_COUNTRIES 와 동기화). 주요 교역국 우선.
const COUNTRIES = [
  ["KR", "🇰🇷 한국"],
  ["US", "🇺🇸 미국"],
  ["EU", "🇪🇺 유럽연합"],
  ["CN", "🇨🇳 중국"],
  ["JP", "🇯🇵 일본"],
  ["VN", "🇻🇳 베트남"],
  ["TW", "🇹🇼 대만"],
  ["AU", "🇦🇺 호주"],
  ["IN", "🇮🇳 인도"],
  ["TH", "🇹🇭 태국"],
  ["ID", "🇮🇩 인도네시아"],
  ["SG", "🇸🇬 싱가포르"],
  ["MY", "🇲🇾 말레이시아"],
  ["PH", "🇵🇭 필리핀"],
  ["GB", "🇬🇧 영국"],
  ["CA", "🇨🇦 캐나다"],
  ["MX", "🇲🇽 멕시코"],
  ["BR", "🇧🇷 브라질"],
  ["CL", "🇨🇱 칠레"],
  ["PE", "🇵🇪 페루"],
  ["CO", "🇨🇴 콜롬비아"],
  ["TR", "🇹🇷 튀르키예"],
  ["AE", "🇦🇪 아랍에미리트"],
  ["IL", "🇮🇱 이스라엘"],
  ["CH", "🇨🇭 스위스"],
  ["NO", "🇳🇴 노르웨이"],
  ["NZ", "🇳🇿 뉴질랜드"],
  ["RU", "🇷🇺 러시아"],
  ["SA", "🇸🇦 사우디아라비아"],
  ["ZA", "🇿🇦 남아프리카공화국"],
  ["MM", "🇲🇲 미얀마"],
  ["KH", "🇰🇭 캄보디아"],
  ["BN", "🇧🇳 브루나이"],
  ["LA", "🇱🇦 라오스"],
  ["IS", "🇮🇸 아이슬란드"],
  ["PA", "🇵🇦 파나마"],
  ["CR", "🇨🇷 코스타리카"],
  ["HN", "🇭🇳 온두라스"],
  ["SV", "🇸🇻 엘살바도르"],
  ["NI", "🇳🇮 니카라과"],
  ["UZ", "🇺🇿 우즈베키스탄"],
  ["BD", "🇧🇩 방글라데시"],
  ["MN", "🇲🇳 몽골"],
];

// 코드 포맷: 8537103000 → 8537.10.3000 / 853710 → 8537.10 / 8537 → 8537
function fmtCode(c) {
  const s = String(c || "");
  if (s.length <= 4) return s;
  if (s.length <= 6) return `${s.slice(0, 4)}.${s.slice(4)}`;
  return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6)}`;
}

function CountryColumn({
  country,
  onCountryChange,
  queryCode,
  onLoaded,
  onOpenDetail,
  onOpenForeign,
  lang,
}) {
  const nameOf = (row) =>
    (lang === "Eng" ? row.nameEn : lang === "Org" ? row.nameOrg : row.nameKo) ||
    "";
  const [state, setState] = useState({
    loading: false,
    codes: [],
    heading6: null,
    precedents: [],
    precedentCount: 0,
    err: null,
  });

  const load = useCallback(() => {
    if (!queryCode) {
      setState({ loading: false, codes: [], heading6: null, err: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, err: null }));
    fetch(
      `/api/onto/hs-compare?q=${encodeURIComponent(queryCode)}&country=${country}`,
    )
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((d) => {
        setState({
          loading: false,
          codes: d.codes || [],
          heading6: d.heading6 || null,
          precedents: d.precedents || [],
          precedentCount: d.precedentCount || 0,
          err: null,
        });
        onLoaded && onLoaded(country, d.codes || []);
      })
      .catch((e) => {
        setState({
          loading: false,
          codes: [],
          heading6: null,
          precedents: [],
          precedentCount: 0,
          err: String(e.message || e),
        });
        onLoaded && onLoaded(country, null);
      });
  }, [country, queryCode, onLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 240,
        display: "flex",
        flexDirection: "column",
        maxHeight: "100%",
      }}
    >
      <div
        style={{
          padding: 10,
          borderBottom: "1px solid var(--audit-border)",
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <select
          value={country}
          onChange={(e) => onCountryChange(e.target.value)}
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: 600,
            padding: "6px 8px",
            borderRadius: 6,
            border: "1px solid var(--audit-border)",
            background: "var(--audit-bg)",
            color: "var(--audit-text)",
          }}
        >
          {COUNTRIES.map(([c, label]) => (
            <option key={c} value={c}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {state.heading6 && (
        <div
          style={{
            padding: "7px 10px",
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--audit-text-muted)",
            borderBottom: "1px solid var(--audit-border)",
            lineHeight: 1.45,
            background: "var(--audit-bg)",
          }}
          title="HS 6자리 국제공통 품목명 (한국 기준)"
        >
          {state.heading6}
        </div>
      )}
      <div style={{ overflow: "auto", padding: 6 }}>
        {state.loading && (
          <div
            style={{
              padding: 16,
              fontSize: 12,
              color: "var(--audit-text-muted)",
            }}
          >
            불러오는 중…
          </div>
        )}
        {state.err && (
          <div style={{ padding: 16, fontSize: 12, color: "#d93025" }}>
            조회 실패: {state.err}
          </div>
        )}
        {!state.loading &&
          !state.err &&
          state.codes.length === 0 &&
          queryCode && (
            <div
              style={{
                padding: 16,
                fontSize: 12,
                color: "var(--audit-text-muted)",
              }}
            >
              결과 없음
            </div>
          )}
        <table
          style={{
            width: "100%",
            fontSize: 12,
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: 48 }} />
            <col style={{ width: 36 }} />
            <col style={{ width: 50 }} />
            <col />
          </colgroup>
          <thead>
            <tr style={{ color: "var(--audit-text-faint)", fontSize: 12.5 }}>
              <th style={segTh}>호</th>
              <th style={segTh}>소호</th>
              <th style={segTh}>세번</th>
              <th style={{ ...segTh, textAlign: "left", paddingLeft: 4 }}>
                품명
              </th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // UNIPASS 스타일 — 호/소호 반복 억제(바뀔 때만 표시), 품명 계층 들여쓰기.
              let prev = ["", "", ""];
              return state.codes.map((row) => {
                const exact = row.code === queryCode;
                const countrySpecific = row.level > 6;
                const clickable = !!row.srno;
                const seg = row.seg || ["", "", ""];
                const show0 = seg[0] && seg[0] !== prev[0];
                const show1 =
                  seg[1] && (seg[1] !== prev[1] || seg[0] !== prev[0]);
                const cells = [
                  show0 ? seg[0] : "",
                  show1 ? seg[1] : "",
                  seg[2] || "",
                ];
                prev = [seg[0] || prev[0], seg[1] || prev[1], seg[2]];
                const depth = (seg[1] ? 1 : 0) + (seg[2] ? 1 : 0); // 들여쓰기 단계
                const onClk = () =>
                  clickable &&
                  onOpenForeign &&
                  onOpenForeign({
                    country,
                    code: row.code,
                    srno: row.srno,
                    ym: row.ym,
                  });
                return (
                  <tr
                    key={row.code}
                    onClick={onClk}
                    title={
                      clickable
                        ? "클릭 — 관세율 상세(협정세율·수출 특혜 포함)"
                        : ""
                    }
                    style={{
                      background: exact
                        ? "var(--audit-accent-bg)"
                        : "transparent",
                      cursor: clickable ? "pointer" : "default",
                    }}
                  >
                    {[0, 1, 2].map((si) => (
                      <td
                        key={si}
                        style={{
                          padding: "6px 4px",
                          textAlign: "center",
                          fontFamily: "JetBrains Mono, monospace",
                          fontSize: 13,
                          fontWeight: si === 0 ? 700 : 500,
                          color:
                            si === 0
                              ? "var(--audit-text)"
                              : "var(--audit-text-muted)",
                          whiteSpace: "nowrap",
                          verticalAlign: "top",
                        }}
                      >
                        {cells[si]}
                      </td>
                    ))}
                    <td
                      style={{
                        padding: "6px 6px",
                        paddingLeft: 6 + depth * 13,
                        verticalAlign: "top",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          color: countrySpecific
                            ? "var(--audit-accent)"
                            : "var(--audit-text)",
                          fontWeight: depth === 0 ? 600 : 400,
                          lineHeight: 1.45,
                          wordBreak: "keep-all",
                        }}
                      >
                        {nameOf(row) || ""}
                      </span>
                      {row.rate && (
                        <span
                          style={{
                            marginLeft: 5,
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 12,
                            color: "var(--audit-text-faint)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          · {row.rate}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>

        {state.precedentCount > 0 && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 8,
              borderTop: "1px solid var(--audit-border)",
            }}
          >
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: "var(--audit-text-muted)",
                marginBottom: 6,
              }}
            >
              이 나라 분류사례 {state.precedentCount.toLocaleString()}건
            </div>
            {state.precedents.map((p) => (
              <button
                key={p.caseId}
                onClick={() => onOpenDetail && onOpenDetail(p.caseId)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background: "transparent",
                  textDecoration: "none",
                  padding: "4px 6px",
                  borderRadius: 5,
                  marginBottom: 2,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
                title="클릭 — 상세내역 보기"
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--audit-accent-bg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <span
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: "var(--audit-accent)",
                  }}
                >
                  {fmtCode(p.code)}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--audit-text-muted)",
                    marginLeft: 6,
                  }}
                >
                  {p.date ? String(p.date).slice(0, 4) : ""}
                </span>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--audit-text-faint)",
                    lineHeight: 1.4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.title}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// 긴 판례 텍스트 → 읽기 쉬운 단락. 한국어 문장 종결 기준 분리(영문 약어 마침표 회피).
function toParagraphs(text) {
  const t = String(text || "").trim();
  if (!t) return [];
  const sents = t
    .split(/(?<=[다요음됨함됩])\.\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (sents.length <= 1) return [t];
  const paras = [];
  for (let i = 0; i < sents.length; i += 2) {
    let p = sents.slice(i, i + 2).join(". ");
    if (!/[.?!]$/.test(p)) p += ".";
    paras.push(p);
  }
  return paras;
}

// 선례 상세내역 모달 — precedents-detail 풀텍스트.
function PrecedentDetailModal({ caseId, onClose }) {
  const [d, setD] = useState({ loading: true, data: null, err: null });
  useEffect(() => {
    if (!caseId) return;
    setD({ loading: true, data: null, err: null });
    fetch(`/api/onto/precedents-detail?case_id=${encodeURIComponent(caseId)}`)
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((data) => setD({ loading: false, data, err: null }))
      .catch((e) =>
        setD({ loading: false, data: null, err: String(e.message || e) }),
      );
  }, [caseId]);
  if (!caseId) return null;
  const j = d.data || {};
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="audit-root"
        style={{
          background: "var(--audit-bg-elevated)",
          borderRadius: 12,
          maxWidth: 720,
          width: "100%",
          maxHeight: "85vh",
          overflow: "auto",
          padding: 24,
          boxShadow: "0 12px 48px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <h2
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "var(--audit-text)",
              margin: 0,
              lineHeight: 1.4,
            }}
          >
            분류사례 상세
          </h2>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              cursor: "pointer",
              color: "var(--audit-text-muted)",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {d.loading && (
          <div
            style={{
              padding: 20,
              color: "var(--audit-text-muted)",
              fontSize: 13,
            }}
          >
            불러오는 중…
          </div>
        )}
        {d.err && (
          <div style={{ padding: 20, color: "#d93025", fontSize: 13 }}>
            오류: {d.err}
          </div>
        )}
        {d.data &&
          (() => {
            const body = (j.description || "").trim();
            const reasoning = (j.reasoning || "").trim();
            const titleClean = (j.title || "").trim();
            const primary = body || reasoning || titleClean;
            const showReasoning =
              reasoning &&
              reasoning !== body &&
              !body.includes(reasoning.slice(0, 30));
            return (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "var(--audit-text)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    marginBottom: 10,
                  }}
                >
                  {j.hsk && (
                    <span
                      style={{
                        fontFamily: "JetBrains Mono, monospace",
                        fontWeight: 700,
                        color: "var(--audit-accent)",
                        fontSize: 14,
                      }}
                    >
                      {fmtCode(j.hsk)}
                    </span>
                  )}
                  {j.decision_date && (
                    <span
                      style={{ color: "var(--audit-text-muted)", fontSize: 12 }}
                    >
                      {String(j.decision_date).slice(0, 10)}
                    </span>
                  )}
                  {j.applied_gri && (
                    <span
                      style={{
                        fontSize: 12.5,
                        color: "var(--audit-text-muted)",
                        border: "1px solid var(--audit-border)",
                        borderRadius: 4,
                        padding: "1px 6px",
                      }}
                    >
                      GRI {j.applied_gri}
                    </span>
                  )}
                </div>
                <div style={{ maxWidth: 600 }}>
                  {toParagraphs(primary).map((p, i) => (
                    <p
                      key={i}
                      style={{
                        margin: "0 0 12px",
                        fontSize: 14,
                        lineHeight: 1.85,
                        color: "var(--audit-text)",
                      }}
                    >
                      {p}
                    </p>
                  ))}
                  {showReasoning && (
                    <div style={{ marginTop: 14 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "var(--audit-text-muted)",
                          marginBottom: 6,
                        }}
                      >
                        분류 이유
                      </div>
                      {toParagraphs(reasoning).map((p, i) => (
                        <p
                          key={i}
                          style={{
                            margin: "0 0 12px",
                            fontSize: 14,
                            lineHeight: 1.85,
                            color: "var(--audit-text-muted)",
                          }}
                        >
                          {p}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                {j.status === "superseded" && j.amendment && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "9px 10px",
                      border: "1px solid var(--audit-border)",
                      borderLeft: "3px solid var(--audit-text)", // 주황 금지 — 회색 상자 + 검은 선(2026-10-02)
                      borderRadius: 6,
                      background: "var(--audit-bg-subtle, #f3f4f6)",
                      color: "var(--audit-text)",
                    }}
                  >
                    {j.amendment.integrity_status === "matched" ? (
                      <b>변경 후 세번 {j.amendment.hs_after}</b>
                    ) : (
                      <b>변경고시 연결 검증 필요 — 변경후 세번 자동 표시 중지</b>
                    )}
                    <div style={{ marginTop: 6 }}>
                      <AmendmentOriginalButton amendment={j.amendment} hsk={j.hsk} />
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <SourceEvidenceLink
                    compact
                    {...precedentEvidence({
                      decisionUrl: j.decision_url,
                      caseId: j.case_id || caseId,
                      source: j.source,
                      code: j.hsk || j.hs6,
                      refNo: j.ref_no,
                      officialForeignSearch: true,
                    })}
                  />
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
}

// 외국 관세율 상세 모달 — 008Q(협정세율·한국발 특혜·기본/특별세율) HTML.
function ForeignDetailModal({ target, onClose }) {
  const [d, setD] = useState({ loading: true, html: "", err: null });
  useEffect(() => {
    if (!target) return;
    setD({ loading: true, html: "", err: null });
    const { country, code, srno, ym } = target;
    fetch(
      `/api/onto/hs-foreign-detail?hsSgn=${code}&country=${country}&srno=${srno || ""}&ym=${ym || ""}`,
    )
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((j) => setD({ loading: false, html: j.html || "", err: null }))
      .catch((e) =>
        setD({ loading: false, html: "", err: String(e.message || e) }),
      );
  }, [target]);
  if (!target) return null;
  const label =
    COUNTRIES.find(([c]) => c === target.country)?.[1] || target.country;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="audit-root"
        style={{
          background: "#fff",
          borderRadius: 12,
          maxWidth: 820,
          width: "100%",
          maxHeight: "88vh",
          overflow: "auto",
          padding: 20,
          boxShadow: "0 12px 48px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <h2
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "var(--audit-text)",
              margin: 0,
            }}
          >
            {label} 관세율 상세{" "}
            <span
              style={{
                fontFamily: "JetBrains Mono, monospace",
                color: "var(--audit-accent)",
              }}
            >
              {fmtCode(target.code)}
            </span>
          </h2>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              cursor: "pointer",
              color: "var(--audit-text-muted)",
            }}
          >
            ×
          </button>
        </div>
        {d.loading && (
          <div
            style={{
              padding: 20,
              color: "var(--audit-text-muted)",
              fontSize: 13,
            }}
          >
            불러오는 중…
          </div>
        )}
        {d.err && (
          <div style={{ padding: 20, color: "#d93025", fontSize: 13 }}>
            오류: {d.err}
          </div>
        )}
        {!d.loading && !d.err && (
          <>
            <style>{FRCY_CSS}</style>
            <div
              className="frcy-detail"
              dangerouslySetInnerHTML={{ __html: d.html }}
            />
          </>
        )}
      </div>
    </div>
  );
}

// 외국상세 HTML(표·제목·목록)에 CSS 주입 — UNIPASS CSS 부재로 뭉개지는 것 방지.
const FRCY_CSS = `
.frcy-detail { font-size: 13.5px; line-height: 1.75; color: var(--audit-text); }
.frcy-detail h3 { font-size: 14.5px; font-weight: 700; margin: 20px 0 9px; padding-bottom: 5px; border-bottom: 2px solid var(--audit-border); color: var(--audit-text); }
.frcy-detail h3:first-child { margin-top: 0; }
.frcy-detail table { width: 100%; border-collapse: collapse; margin: 10px 0 18px; }
.frcy-detail caption { text-align: left; font-weight: 700; font-size: 13px; margin-bottom: 6px; color: var(--audit-text-muted); }
.frcy-detail th, .frcy-detail td { border: 1px solid var(--audit-border); padding: 8px 11px; font-size: 13px; text-align: left; vertical-align: top; line-height: 1.6; }
.frcy-detail th { background: var(--audit-bg); font-weight: 600; color: var(--audit-text-muted); }
.frcy-detail .textCenter { text-align: center; }
.frcy-detail ul { margin: 6px 0 14px; padding-left: 20px; }
.frcy-detail li { margin-bottom: 6px; line-height: 1.7; }
.frcy-detail p { margin: 8px 0; line-height: 1.75; }
.frcy-detail a { color: var(--audit-accent); text-decoration: underline; }
.frcy-detail strong { color: var(--audit-text); }
.frcy-detail br { display: block; content: ""; margin-top: 4px; }
`;

// URL ?q= & ?cnt=KR,US,EU 딥링크 파싱 (속견표·세번상세에서 진입).
function initialFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const q = (p.get("q") || "").replace(/[^0-9]/g, "");
    const cnt = (p.get("cnt") || "")
      .split(",")
      .map((c) => c.trim().toUpperCase())
      .filter((c) => COUNTRIES.some(([cc]) => cc === c));
    return { q, cnt };
  } catch {
    return { q: "", cnt: [] };
  }
}

export default function HsComparePage() {
  const _init = initialFromUrl();
  const [input, setInput] = useState(_init.q || "");
  const [queryCode, setQueryCode] = useState(_init.q || "");
  const [cols, setCols] = useState(
    _init.cnt && _init.cnt.length ? _init.cnt : ["KR", "US", "EU"],
  );

  // URL 이 바뀌면(딥링크 재진입) 동기화
  useEffect(() => {
    const onPop = () => {
      const { q, cnt } = initialFromUrl();
      if (q) {
        setInput(q);
        setQueryCode(q);
      }
      if (cnt && cnt.length) setCols(cnt);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ① 품명으로 시작 — 코드면 바로, 제품명(텍스트)이면 AI/검색으로 6자리 해석
  const [resolving, setResolving] = useState(false);
  const [matchInfo, setMatchInfo] = useState(null);
  const search = async () => {
    const raw = input.trim();
    if (!raw) return;
    const numeric = raw.replace(/[^0-9]/g, "");
    if (/^[0-9.\s]+$/.test(raw) && numeric.length >= 2) {
      setMatchInfo(null);
      setQueryCode(numeric);
      return;
    }
    // 제품명 → 우리 검색/AI 로 대표 6자리 찾기
    setResolving(true);
    setMatchInfo(null);
    try {
      const r = await fetch(
        `/api/onto/tariff-search?q=${encodeURIComponent(raw)}&mode=hybrid&limit=1`,
        { headers: userAuthHeaders() },
      );
      const d = await r.json();
      const top = (d.items || [])[0];
      const code =
        top && String(top.hsk || top.hs6 || "").replace(/[^0-9]/g, "");
      if (code && code.length >= 6) {
        const c6 = code.slice(0, 6);
        setQueryCode(c6);
        const nm = (top.desc_ko || top.desc || "")
          .split(/\/?\s*양허유형/)[0]
          .trim();
        setMatchInfo({ q: raw, code: c6, name: nm });
      } else {
        setMatchInfo({ q: raw, none: true });
      }
    } catch {
      setMatchInfo({ q: raw, none: true });
    } finally {
      setResolving(false);
    }
  };

  const setColAt = (i, c) =>
    setCols((prev) => prev.map((x, idx) => (idx === i ? c : x)));
  const addCol = () =>
    setCols((prev) =>
      prev.length < 5
        ? [...prev, COUNTRIES.find(([c]) => !prev.includes(c))?.[0] || "JP"]
        : prev,
    );
  const removeCol = () =>
    setCols((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));

  // ② 차이 요약 — 컬럼별 국가 세분 수(최심 레벨 코드 수) 집계
  const [subdiv, setSubdiv] = useState({});
  useEffect(() => {
    setSubdiv({});
  }, [queryCode]);
  const onLoaded = useCallback((country, codes) => {
    if (!codes) {
      setSubdiv((s) => ({ ...s, [country]: null }));
      return;
    }
    const maxL = codes.reduce((m, c) => Math.max(m, c.level), 0);
    const leaves = codes.filter((c) => c.level === maxL && c.level > 6).length;
    setSubdiv((s) => ({ ...s, [country]: leaves }));
  }, []);
  const labelOf = (c) => COUNTRIES.find(([cc]) => cc === c)?.[1] || c;
  const hs6 = queryCode.slice(0, 6);
  const [detailCase, setDetailCase] = useState(null);
  const [foreignTarget, setForeignTarget] = useState(null);
  const [lang, setLang] = useState("Kor"); // Kor | Eng | Org

  return (
    <div
      className="audit-root"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "var(--audit-bg)",
        padding: 20,
      }}
    >
      <div style={{ marginBottom: 4 }}>
        <h1
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "var(--audit-text)",
            margin: 0,
          }}
        >
          HS 비교{" "}
          <span
            style={{
              fontSize: 12,
              fontWeight: 400,
              color: "var(--audit-text-muted)",
            }}
          >
            · 나라별 세번 비교
          </span>
        </h1>
        <p
          style={{
            fontSize: 12,
            color: "var(--audit-text-muted)",
            margin: "4px 0 12px",
          }}
        >
          HS 6자리는 국제 공통, 7~10자리는 국가별 세분입니다. 코드를 입력하면
          선택한 나라들의 세번 구조·세율을 나란히 비교합니다. (관세청 세계HS
          실시간)
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="제품명 또는 HS 코드 (예: 아이섀도우, 8537)"
          style={{
            flex: "1 1 280px",
            maxWidth: 360,
            fontSize: 14,
            padding: "9px 12px",
            borderRadius: 8,
            border: "1px solid var(--audit-border)",
            background: "var(--audit-bg-elevated)",
            color: "var(--audit-text)",
          }}
        />
        <button
          onClick={search}
          disabled={resolving}
          style={{
            padding: "9px 18px",
            border: "none",
            borderRadius: 8,
            background: "var(--audit-accent)",
            color: "#fff",
            fontSize: 13.5,
            fontWeight: 700,
            cursor: resolving ? "default" : "pointer",
            opacity: resolving ? 0.7 : 1,
          }}
        >
          {resolving ? "찾는 중…" : "조회"}
        </button>
        <a
          href={
            queryCode
              ? `https://unipass.customs.go.kr/clip/hsinfosrch/openULS0201011Q.do?searchVal=${queryCode}`
              : "https://unipass.customs.go.kr/clip/hsinfosrch/openULS0201011Q.do"
          }
          target="_blank"
          rel="noopener noreferrer"
          style={{
            ...colBtn,
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            color: "var(--audit-text-muted)",
          }}
          title="이 데이터의 출처 — 관세청 UNIPASS 세계HS 원문(새 탭). 우리는 여기에 분류선례·차이요약·품명까지 더합니다."
        >
          관세청 원문 ↗
        </a>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 6,
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              border: "1px solid var(--audit-border)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {[
              ["Kor", "국문"],
              ["Eng", "영문"],
              ["Org", "원문"],
            ].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setLang(v)}
                style={{
                  border: "none",
                  padding: "7px 11px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: "pointer",
                  background:
                    lang === v ? "var(--audit-accent)" : "var(--audit-bg)",
                  color: lang === v ? "#fff" : "var(--audit-text-muted)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <button onClick={removeCol} style={colBtn} title="국가 컬럼 제거">
            – 국가
          </button>
          <button onClick={addCol} style={colBtn} title="국가 컬럼 추가">
            + 국가
          </button>
        </div>
      </div>

      {matchInfo && !matchInfo.none && (
        <div
          style={{
            marginBottom: 10,
            fontSize: 12.5,
            color: "var(--audit-text-muted)",
          }}
        >
          <b style={{ color: "var(--audit-text)" }}>"{matchInfo.q}"</b> →{" "}
          <span
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontWeight: 700,
              color: "var(--audit-accent)",
            }}
          >
            {fmtCode(matchInfo.code)}
          </span>
          {matchInfo.name ? ` · ${matchInfo.name}` : ""} 로 비교합니다.
        </div>
      )}
      {matchInfo && matchInfo.none && (
        <div style={{ marginBottom: 10, fontSize: 12.5, color: "#d93025" }}>
          "{matchInfo.q}" 에 맞는 HS 코드를 찾지 못했습니다. 다른 품명이나 HS
          코드를 입력해 보세요.
        </div>
      )}

      {queryCode && (
        <div
          style={{
            marginBottom: 12,
            padding: "9px 13px",
            borderRadius: 8,
            background: "var(--audit-accent-bg)",
            fontSize: 12.5,
            color: "var(--audit-text)",
            lineHeight: 1.6,
          }}
        >
          <b>HS {fmtCode(hs6)}</b> 까지는 전 세계 공통, 이후는 나라별
          세분입니다.
          <span style={{ marginLeft: 8, color: "var(--audit-text-muted)" }}>
            국가별 세분 수:
          </span>{" "}
          {cols.map((c, i) => (
            <span key={c + i} style={{ marginRight: 10, whiteSpace: "nowrap" }}>
              {labelOf(c)}{" "}
              <b style={{ color: "var(--audit-accent)" }}>
                {subdiv[c] == null ? "…" : `${subdiv[c]}개`}
              </b>
            </span>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flex: 1, minHeight: 0 }}>
        {cols.map((c, i) => (
          <CountryColumn
            key={i}
            country={c}
            queryCode={queryCode}
            onCountryChange={(nc) => setColAt(i, nc)}
            onLoaded={onLoaded}
            onOpenDetail={setDetailCase}
            onOpenForeign={setForeignTarget}
            lang={lang}
          />
        ))}
      </div>

      {detailCase && (
        <PrecedentDetailModal
          caseId={detailCase}
          onClose={() => setDetailCase(null)}
        />
      )}
      {foreignTarget && (
        <ForeignDetailModal
          target={foreignTarget}
          onClose={() => setForeignTarget(null)}
        />
      )}
    </div>
  );
}

const segTh = {
  fontSize: 12,
  fontWeight: 600,
  textAlign: "center",
  padding: "0 4px 6px",
  borderBottom: "1px solid var(--audit-border)",
  color: "var(--audit-text-faint)",
};

const colBtn = {
  padding: "7px 12px",
  border: "1px solid var(--audit-border)",
  borderRadius: 8,
  background: "var(--audit-bg)",
  color: "var(--audit-text)",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
};
